import * as React from "react";
import { useHistory } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";

import { displayAttributesApiCall, fetchFileApiCall, savedSearchApiCall } from "../redux/apiCalls";
import { storeTooltipAttributesAttachFromIci, startFetchingSavedSearchDataAttachFromIci } from "../redux/slice";
import { setSelectedSavedSearchData, transformSavedSearchData, updateSavedSearchData } from "../utils";
import { TEXT } from "../../../shared/constants/texts";
import { logError } from "../../../shared/services/errorLogging";

const SavedSearch: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const state = useSelector((state: any) => state.attachFromIciSlice);
  const { isFetchingSavedSearchData, savedSearchData, selectedSavedSearch } = state;
  const errorValidationMsg =
    useSelector((state: any) => state.errorValidation.validationError) ||
    JSON.parse(localStorage.getItem(TEXT.LOCAL_STORAGE_VALIDATION_MSG_KEY));
  const apiCallParams = {
    dispatch: dispatch,
    history: history,
    errorValidationMsg: errorValidationMsg,
  };

  React.useEffect(() => {
    getSavedSearches();
  }, []);

  const getSavedSearches = async () => {
    dispatch(startFetchingSavedSearchDataAttachFromIci());
    const savedSearchResponse = await savedSearchApiCall({
      ...apiCallParams,
      retryFunctionCall: () => getSavedSearches(),
    });
    const { response, error } = savedSearchResponse;
    if (response) {
      const transformedSavedSearchData = transformSavedSearchData(response);
      updateSavedSearchData(transformedSavedSearchData);
      const displayAttributesResponse = await displayAttributesApiCall({ ...apiCallParams });
      const { response: attributeResponse, error: attributeError } = displayAttributesResponse;
      const transformedAttributeResponse = JSON.parse(attributeResponse);
      if (transformedAttributeResponse) {
        const agreementListAttributesFilePath =
          transformedAttributeResponse.ICMAgreementListAttributes ||
          transformedAttributeResponse.ICMOutlookAddinDisplayAttributes_ICMAgreementListAttributes;
        const agreementDetailsAttributesFilePath =
          transformedAttributeResponse.ICMAgreementDetailsAttributes ||
          transformedAttributeResponse.ICMOutlookAddinDisplayAttributes_ICMAgreementDetailsAttributes;
        const attributeListResponse = await fetchFileApiCall({
          ...apiCallParams,
          filePath: agreementListAttributesFilePath,
        });
        const { response: listResponse, error: listError } = attributeListResponse;
        const attributeDetailsResponse = await fetchFileApiCall({
          ...apiCallParams,
          filePath: agreementDetailsAttributesFilePath,
        });
        const { response: detailsResponse, error: detailsError } = attributeDetailsResponse;
        if (listResponse && detailsResponse) {
          let transformedAttributeListResponse = listResponse;
          let transformedAttributeDetailsResponse = detailsResponse;
          if ("$value" in transformedAttributeListResponse) {
            transformedAttributeListResponse = transformedAttributeListResponse["$value"];
          }
          if ("$value" in transformedAttributeDetailsResponse) {
            transformedAttributeDetailsResponse = transformedAttributeDetailsResponse["$value"];
          }
          const agreementListAttributes = JSON.parse(atob(transformedAttributeListResponse));
          const agreementDetailsAttributes = JSON.parse(atob(transformedAttributeDetailsResponse));
          let tooltipAttributes = agreementListAttributes.concat(agreementDetailsAttributes);
          tooltipAttributes = tooltipAttributes
            .filter((item, index) => tooltipAttributes.findIndex((itemIndex) => item.Key === itemIndex.Key) === index)
            .map((attr) => {
              return { Key: attr.Key.toLowerCase(), Value: attr.Value };
            });
          dispatch(storeTooltipAttributesAttachFromIci(tooltipAttributes));
        }
        if (listError || detailsError) {
          dispatch(storeTooltipAttributesAttachFromIci([]));
          logError({
            dispatch: dispatch,
            err: error,
            callerInfo: {
              callerFunction:
                "ClientApp|src|taskpane|containers|attach-from-ici|saved-search|SavedSearch - getSavedSearches",
              callerApi: "Agreement/GetFile?filePath=",
            },
          });
        }
      }
      if (attributeError) {
        dispatch(storeTooltipAttributesAttachFromIci([]));
        logError({
          dispatch: dispatch,
          err: error,
          callerInfo: {
            callerFunction:
              "ClientApp|src|taskpane|containers|attach-from-ici|saved-search|SavedSearch - getSavedSearches",
            callerApi: "Masterdata/Get?id=&contractType=&includeChildCollections=True&fetchAuditEntries=False",
          },
        });
      }
    }
    if (error) {
      dispatch(storeTooltipAttributesAttachFromIci([]));
      updateSavedSearchData([]);
      logError({
        dispatch: dispatch,
        err: error,
        callerInfo: {
          callerFunction:
            "ClientApp|src|taskpane|containers|attach-from-ici|saved-search|SavedSearch - getSavedSearches",
          callerApi: "SavedSearches/ForAddIn?id=&contractType=",
        },
      });
    }
  };

  const onSavedSearchChangeHandler = (data) => {
    if (selectedSavedSearch.id !== data.id) {
      setSelectedSavedSearchData(data);
    }
  };

  return (
    <div className="saved-search-container-attach-from-ici">
      <label htmlFor="saved-search-attach-from-ici">{TEXT.SAVED_SEARCH}</label>
      <div className="saved-search-a11y-attach-from-ici" tabIndex={1}>
        <Select
          aria-labelledby="saved-search-attach-from-ici"
          inputId="saved-search-attach-from-ici"
          name="saved-search-attach-from-ici"
          placeholder={TEXT.PLACEHOLDER_SAVED_SEARCH}
          options={savedSearchData}
          defaultValue={isFetchingSavedSearchData ? "" : savedSearchData[0]}
          styles={savedSearchDropdownStyles}
          isLoading={isFetchingSavedSearchData}
          value={selectedSavedSearch}
          onChange={(option) => onSavedSearchChangeHandler(option)}
          noOptionsMessage={() => <p className="empty-message">{TEXT.NO_SAVED_SEARCHES_FOUND}</p>}
          components={{
            IndicatorSeparator: () => null,
          }}
          tabIndex={1}
          tabSelectsValue={true}
        />
      </div>
    </div>
  );
};

export default SavedSearch;

const savedSearchDropdownStyles = {
  menu: (styles: any) => ({
    ...styles,
    border: "0.0625rem solid #a6a6a6",
    boxShadow: "none",
    marginTop: "-0.25rem",
  }),
  control: (styles: any) => ({
    ...styles,
    marginTop: "0.125rem",
    backgroundColor: "#fff",
    border: "0.0625rem solid #a6a6a6",
    boxShadow: "none",
    "&:hover": {
      border: "0.0625rem solid #a6a6a6",
    },
    fontSize: "0.75rem",
    borderRadius: "0",
    height: 32,
    minHeight: 32,
  }),
  valueContainer: (styles) => ({
    ...styles,
    height: 32,
    padding: 4,
    marginTop: "-0.125rem",
  }),
  indicatorsContainer: (styles) => ({
    ...styles,
    height: 32,
    marginTop: "-0.125rem",
  }),
  option: (styles: { [x: string]: any }, { isFocused, isSelected }: any) => {
    return {
      ...styles,
      backgroundColor: isSelected ? "#0078d7" : isFocused ? "#f3f2f1" : "#fff",
      outline: isFocused ? "1px solid #00a2ed" : null,
      cursor: isFocused ? "pointer" : "default",
      ":active": {
        ...styles[":active"],
        backgroundColor: "#0078d7",
      },
      borderRadius: "0.25rem",
      fontSize: "0.75rem",
    };
  },
};
